import { useCallback, useEffect, useRef, useState } from 'react';

import {
    useCoverageMapClientDensity,
    useCoverageMapPoints,
} from '@apps/web/features/coverage/api/coverage_queries';
import { type CoverageRow } from '@apps/web/features/coverage/model/coverage_types';
import {
    requestErrorMessage,
    type RequestStatus,
} from '@apps/web/features/coverage/model/request_model';
import { getApiClient } from '@apps/web/lib/api_client';
import { isTransparentRateLimitError } from '@apps/web/lib/known_api_errors';
import { useMode } from '@apps/web/lib/mode_store';

export type CoverageMapStatus = 'idle' | 'loading' | 'error' | 'ready';
export type CoverageClientDensityStatus =
    | 'idle'
    | 'loading'
    | 'error'
    | 'ready';

export function useCoverageMap({
    organizationId,
    enabled,
    includeInactive,
    plotClients,
    rows,
    visibleCaregiverIds,
    isAdmin,
}: {
    readonly organizationId: string | null;
    readonly enabled: boolean;
    readonly includeInactive: boolean;
    readonly plotClients: boolean;
    readonly rows: ReadonlyArray<CoverageRow>;
    readonly visibleCaregiverIds: ReadonlySet<string>;
    readonly isAdmin: boolean;
}) {
    const mode = useMode();
    const { data, error, hasRequestFailed, isLoading, mutate } =
        useCoverageMapPoints({
            organizationId,
            enabled,
            includeInactive,
        });
    const {
        data: clientDensityData,
        error: clientDensityError,
        isLoading: isClientDensityLoading,
    } = useCoverageMapClientDensity({
        organizationId,
        enabled: enabled && plotClients && mode === 'live',
    });
    const status: CoverageMapStatus =
        !enabled || !organizationId
            ? 'idle'
            : data !== undefined
              ? 'ready'
              : isLoading
                ? 'loading'
                : hasRequestFailed
                  ? 'error'
                  : 'idle';
    const points = status === 'ready' ? (data?.points ?? []) : [];
    const clientDensityCells =
        status === 'ready' ? (clientDensityData?.cells ?? []) : [];
    const clientDensityStatus: CoverageClientDensityStatus =
        !plotClients || mode !== 'live'
            ? 'idle'
            : isClientDensityLoading
              ? 'loading'
              : clientDensityError
                ? 'error'
                : clientDensityData
                  ? 'ready'
                  : 'idle';
    const clientDensityErrorMessage = clientDensityError
        ? requestErrorMessage(
              clientDensityError,
              'We couldn’t load client density. Turn the layer off and on to try again.',
          )
        : null;
    const mappedVisibleCount = points.filter((point) =>
        visibleCaregiverIds.has(point.caregiver_id),
    ).length;
    const unmappedCount = Math.max(0, rows.length - mappedVisibleCount);
    const coverageBanner =
        status === 'ready'
            ? `Showing ${mappedVisibleCount} of ${rows.length} filtered caregivers on the map.` +
              (unmappedCount > 0
                  ? ` ${unmappedCount} ${unmappedCount === 1 ? 'has' : 'have'} no mapped address yet.`
                  : '')
            : '';

    const [geocodeStatus, setGeocodeStatus] = useState<RequestStatus>('idle');
    const [geocodeError, setGeocodeError] = useState<string | null>(null);
    const activeGeocodeRequestRef = useRef<symbol | null>(null);
    useEffect(() => {
        activeGeocodeRequestRef.current = null;
        setGeocodeStatus('idle');
        setGeocodeError(null);
    }, [organizationId, mode, includeInactive]);
    // The geocode kickoff posts to an /admin endpoint, so only platform
    // admins can trigger it; customers just see the coverage counts.
    const canRequestGeocode =
        isAdmin &&
        mode === 'live' &&
        status === 'ready' &&
        (data?.coverage.caregiver_geocode_missing ?? 0) > 0;
    const requestGeocode = useCallback(async () => {
        if (
            !organizationId ||
            !canRequestGeocode ||
            activeGeocodeRequestRef.current
        ) {
            return;
        }
        const requestToken = Symbol('coverage-geocode-request');
        activeGeocodeRequestRef.current = requestToken;
        setGeocodeStatus('pending');
        setGeocodeError(null);
        try {
            await getApiClient().post({
                url: '/admin/geocode/organization',
                body: { organization_id: organizationId },
                reason: 'coverage-geocode',
            });
            if (activeGeocodeRequestRef.current === requestToken) {
                setGeocodeStatus('success');
            }
        } catch (requestError) {
            if (activeGeocodeRequestRef.current === requestToken) {
                if (isTransparentRateLimitError(requestError)) {
                    setGeocodeStatus('idle');
                } else {
                    setGeocodeStatus('error');
                    setGeocodeError(
                        requestErrorMessage(
                            requestError,
                            'We couldn’t start the location update.',
                        ),
                    );
                }
            }
        } finally {
            if (activeGeocodeRequestRef.current === requestToken) {
                activeGeocodeRequestRef.current = null;
            }
        }
    }, [organizationId, canRequestGeocode]);

    return {
        status,
        hasRequestFailed,
        errorMessage:
            status === 'error' && error
                ? requestErrorMessage(
                      error,
                      'We couldn’t load caregiver locations.',
                  )
                : null,
        refreshErrorMessage:
            status === 'ready' && error
                ? 'We couldn’t refresh caregiver locations. Showing previously loaded data.'
                : null,
        points,
        clientDensityCells,
        clientDensityStatus,
        clientDensityErrorMessage,
        coverageBanner,
        retry: () => {
            void mutate();
        },
        canRequestGeocode,
        geocodeStatus,
        geocodeError,
        requestGeocode,
        dismissGeocodeError: () => {
            setGeocodeStatus('idle');
            setGeocodeError(null);
        },
    };
}
